import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  Button,
} from '@mui/material';
import COLORS from '../../../../resources/style/Themes';
import BaseModal from '../../../../components/modals/BaseModal';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import {
  useShowGroupSelectionModal,
  useVisibleGroups,
  useLocationSharingActions,
} from '@/stores/map/locationSharingStore';
import Group from '@/models/Organization/Group';

function LocationSharingGroupSelectionModal() {
  /** @type {[string[], React.Dispatch<React.SetStateAction<string[]>>]} */
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  /** @type {[Group[], React.Dispatch<React.SetStateAction<Group[]>>]} */
  const [validGroups, setValidGroups] = useState([]);
  const { t } = useTranslation();

  const { groups } = useOrganizationUpdaterProvider();

  // Store hooks
  const open = useShowGroupSelectionModal();
  const visibleGroups = useVisibleGroups();
  const locationSharingActions = useLocationSharingActions();

  useEffect(() => {
    if (groups && Array.isArray(groups)) {
      setValidGroups(groups);
    }
  }, [groups]);

  useEffect(() => {
    if (validGroups.length > 0) {
      const allGroupNames = validGroups.map((group) => group.group_name);
      const allSelected = allGroupNames.every((groupName) =>
        selectedGroups.includes(groupName),
      );
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedGroups, validGroups]);

  useEffect(() => {
    if (open) setSelectedGroups([...visibleGroups]);
  }, [open, visibleGroups]);

  /**
   *
   * @param {string} groupId
   */
  const toggleGroup = (groupId) => {
    setSelectedGroups((prev) => {
      if (prev.includes(groupId)) return prev.filter((id) => id !== groupId);
      else return [...prev, groupId];
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) setSelectedGroups([]);
    else {
      const allGroupNames = validGroups.map((group) => group.group_name);
      setSelectedGroups(allGroupNames);
    }
  };

  const handleApply = () => {
    validGroups.forEach((group) => {
      const isCurrentlyVisible = visibleGroups.includes(group.group_name);
      const shouldBeVisible = selectedGroups.includes(group.group_name);
      if (isCurrentlyVisible !== shouldBeVisible)
        locationSharingActions.handleGroupToggle(group.group_name);
    });

    locationSharingActions.handleGroupSelectionComplete();
  };

  const footerContent = (
    <Button
      variant="contained"
      onClick={handleApply}
      sx={{
        '&.Mui-disabled': {
          bgcolor: 'rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      Apply
    </Button>
  );

  return (
    <BaseModal
      open={open}
      onClose={locationSharingActions.handleGroupSelectionCancel}
      title={t(StringKeys.SHOW_WORKER_LOCATIONS)}
      onSubmit={handleApply}
      footerContent={footerContent}
      size="sm"
    >
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t(StringKeys.SELECT_GROUPS_TO_DISPLAY)}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectAll}
              onChange={toggleSelectAll}
              sx={{
                color: COLORS.DARK_BLUE,
                '&.Mui-checked': {
                  color: COLORS.BLUE,
                },
              }}
            />
          }
          label={t(StringKeys.SELECT_ALL_GROUPS)}
        />
      </Box>

      <Divider />

      <List sx={{ overflowY: 'auto', maxHeight: '50vh', px: 2 }}>
        {validGroups.map((group) => (
          <ListItem key={group.group_name} sx={{ py: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedGroups.includes(group.group_name)}
                  onChange={() => toggleGroup(group.group_name)}
                  sx={{
                    color: group.color,
                    '&.Mui-checked': {
                      color: group.color,
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    color: selectedGroups.includes(group.group_name)
                      ? group.color
                      : 'text.primary',
                  }}
                >
                  {group.group_name}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
    </BaseModal>
  );
}

export default LocationSharingGroupSelectionModal;
